// IE Hub - Conditional Container Component
// Layout component that conditionally displays content based on error states

import React, { forwardRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert
} from '@mui/material';
import {
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';

interface ConditionalContainerProps {
  /** Main title to display */
  title?: string | undefined;
  /** Description text to show below title */
  description?: string | undefined;
  /** Error message to display when ifError is true */
  errorMessage?: string | undefined;
  /** Controls whether to show error state or normal content */
  ifError?: boolean;
  /** Content to display when not in error state */
  children?: React.ReactNode;
  /** Container variant styling */
  variant?: 'paper' | 'outlined' | 'plain';
  /** Custom styling */
  sx?: Record<string, unknown>;
  /** Error severity level */
  errorSeverity?: 'error' | 'warning';
  /** Show error icon */
  showErrorIcon?: boolean;
  /** Show info icon for normal state */
  showInfoIcon?: boolean;
  /** Custom title typography variant */
  titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2';
  /** Custom description typography variant */
  descriptionVariant?: 'body1' | 'body2' | 'caption';
}

const ConditionalContainer = forwardRef<HTMLDivElement, ConditionalContainerProps>(({
  title,
  description,
  errorMessage,
  ifError = false,
  children,
  variant = 'paper',
  sx = {},
  errorSeverity = 'error',
  showErrorIcon = true,
  showInfoIcon = false,
  titleVariant = 'h5',
  descriptionVariant = 'body2',
  ...props
}, ref) => {
  
  // Determine container component based on variant
  const ContainerComponent = variant === 'plain' ? Box : Paper;
  const containerProps = variant === 'plain' ? {} : {
    elevation: variant === 'paper' ? 1 : 0,
    variant: variant === 'outlined' ? 'outlined' : 'elevation'
  };

  // Container styling
  const containerSx = {
    p: variant === 'plain' ? 0 : 3,
    borderRadius: variant === 'plain' ? 0 : 2,
    ...sx
  };

  // Header content (title + description)
  const headerContent = (title || description) && (
    <Box data-testid="ui-layout-header-content-container-action-1" sx={{ mb: ifError ? 2 : (children ? 3 : 0) }}>
      {title && (
        <Box data-testid="ui-layout-header-content-container-action-2" sx={{ display: 'flex', alignItems: 'center', mb: description ? 1 : 0 }}>
          {showInfoIcon && !ifError && (
            <InfoIcon sx={{ mr: 1, color: 'info.main' }} />
          )}
          <Typography data-testid="ui-layout-header-content-text-action-1" 
            variant={titleVariant} 
            component="h2"
            sx={{ 
              fontWeight: 600,
              color: ifError ? `${errorSeverity}.main` : 'text.primary'
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
      
      {description && !ifError && (
        <Typography data-testid="ui-layout-header-content-text-action-2" 
          variant={descriptionVariant}
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );

  // Error content
  const errorContent = ifError && errorMessage && (
    <Alert 
      severity={errorSeverity}
      icon={showErrorIcon ? <ErrorIcon /> : false}
      sx={{ 
        mb: children ? 2 : 0,
        borderRadius: 1
      }}
    >
      <Typography data-testid="ui-layout-error-content-text-action" variant="body2">
        {errorMessage}
      </Typography>
    </Alert>
  );

  // Main content (children) - always show children, regardless of error state
  const mainContent = children && (
    <Box data-testid="ui-layout-main-content-container-action">
      {children}
    </Box>
  );

  return (
    <ContainerComponent
      ref={ref}
      sx={containerSx}
      {...containerProps}
      {...props}
    >
      {headerContent}
      {errorContent}
      {mainContent}
    </ContainerComponent>
  );
});

ConditionalContainer.displayName = 'ConditionalContainer';

export default ConditionalContainer;

// Type exports for external use
export type { ConditionalContainerProps };
